import { defineStore } from 'pinia'
import { adminAPI } from '@/utils/api'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isAuthenticated: false,
    adminInfo: null,
    loading: false
  }),

  getters: {
    isAdmin: (state) => state.adminInfo?.isAdmin || false,
    adminName: (state) => state.adminInfo?.nickName || '超级管理员'
  },

  actions: {
    // 初始化认证状态
    async initAuth() {
      const token = localStorage.getItem('super_admin_token')
      if (token) {
        await this.checkAuth()
      }
    },

    // 检查认证状态
    async checkAuth() {
      try {
        this.loading = true
        const token = localStorage.getItem('super_admin_token')
        
        if (!token) {
          console.log('🔐 没有找到Token，需要登录')
          this.logout()
          return false
        }

        console.log('🔐 检查认证状态...')

        // 尝试从Server端验证Token
        try {
          const result = await adminAPI.validateAdminToken({ token })
          
          if (result.success && result.data) {
            console.log('✅ Token验证成功')
            this.isAuthenticated = true
            
            // 如果Server端返回了用户信息，使用它
            if (result.data.adminInfo) {
              this.adminInfo = {
                ...result.data.adminInfo,
                isAdmin: true,
                role: 'super_admin'
              }
            } else {
              // 否则从localStorage恢复
              const savedAdminInfo = localStorage.getItem('super_admin_info')
              if (savedAdminInfo) {
                try {
                  this.adminInfo = JSON.parse(savedAdminInfo)
                  console.log('📱 从本地存储恢复管理员信息')
                } catch (e) {
                  console.warn('解析保存的管理员信息失败:', e)
                  this.adminInfo = this.getDefaultAdminInfo()
                }
              } else {
                this.adminInfo = this.getDefaultAdminInfo()
              }
            }
            
            return true
          } else {
            console.warn('⚠️ Token验证失败:', result.message)
            this.logout()
            return false
          }
        } catch (apiError) {
          console.warn('⚠️ Token验证API调用失败，尝试本地恢复:', apiError.message)
          
          // 检查是否是401错误（Token无效/过期）
          if (apiError.status === 401 || apiError.message?.includes('401') || apiError.message?.includes('token')) {
            console.warn('🔐 Token已过期或无效，清除本地状态')
            this.logout()
            return false
          }
          
          // 如果是网络错误等其他问题，尝试从localStorage恢复（离线模式）
          const savedAdminInfo = localStorage.getItem('super_admin_info')
          if (savedAdminInfo && token) {
            try {
              this.adminInfo = JSON.parse(savedAdminInfo)
              this.isAuthenticated = true
              console.log('📱 离线模式：从本地存储恢复认证状态')
              return true
            } catch (e) {
              console.error('解析本地管理员信息失败:', e)
            }
          }
          
          this.logout()
          return false
        }
      } catch (error) {
        console.error('❌ 检查认证状态失败:', error)
        this.logout()
        return false
      } finally {
        this.loading = false
      }
    },

    // 获取默认管理员信息
    getDefaultAdminInfo() {
      return {
        isAdmin: true,
        nickName: '超级管理员',
        username: 'admin',
        role: 'super_admin',
        loginType: 'password'
      }
    },

    // 超级管理员登录
    async login(loginData) {
      try {
        this.loading = true
        
        // 准备Server端API期望的格式
        const serverLoginData = {
          username: loginData.username,
          password: loginData.password
        }
        
        console.log('🔐 开始登录...', { username: serverLoginData.username })
        
        // 调用Server端登录API
        const result = await adminAPI.superAdminLogin(serverLoginData)
        
        console.log('📡 登录响应:', result)
        
        if (result.success && result.data) {
          // Server端返回格式：{ success, data: { token, adminInfo } }
          const { token, adminInfo } = result.data
          
          this.isAuthenticated = true
          this.adminInfo = {
            ...adminInfo,
            isAdmin: true,
            role: 'super_admin'
          }
          
          // 保存token到本地存储
          if (token) {
            localStorage.setItem('super_admin_token', token)
            console.log('💾 Token已保存')
          }
          
          // 保存管理员信息到本地存储
          if (adminInfo) {
            localStorage.setItem('super_admin_info', JSON.stringify(this.adminInfo))
            console.log('👤 管理员信息已保存:', this.adminInfo)
          }
          
          return { success: true }
        } else {
          console.warn('❌ 登录失败:', result.message)
          return { 
            success: false, 
            message: result.message || result.error || '登录失败' 
          }
        }
      } catch (error) {
        console.error('❌ 登录错误:', error)
        return { 
          success: false, 
          message: '网络错误，请稍后重试' 
        }
      } finally {
        this.loading = false
      }
    },

    // 登出
    logout() {
      this.isAuthenticated = false
      this.adminInfo = null
      localStorage.removeItem('super_admin_token')
      localStorage.removeItem('super_admin_info')
    },

    // 更新管理员信息
    updateAdminInfo(adminInfo) {
      this.adminInfo = { ...this.adminInfo, ...adminInfo }
    }
  }
}) 